import { DateTime } from "luxon";
import {
  Event,
  Identifiable,
  Milestone,
  Project,
  Unavailability,
  CalendarHolidays,
  Task,
} from "@planzone/dto";
import {
  CalendarEvent,
  CalendarMilestone,
  CalendarsHolidays,
  CalendarUnavailability,
  CalendarTask,
} from "./types";

const INVALID_DAY = DateTime.utc(1970, 1, 1);

function fromDayMarker(date: Date): DateTime {
  return DateTime.fromJSDate(date, { zone: "utc" });
}

function dayMarkerFromParts(dt: DateTime): Date {
  return DateTime.utc(dt.year, dt.month, dt.day).toJSDate();
}

function utcDate(y: number, m: number, d: number, h = 0, min = 0, s = 0): Date {
  // JS's Date.UTC(y, m, d, ...) rolled out-of-range day values into adjacent
  // months (Date.UTC(y, 5, -4) === May 27; Date.UTC(y, 4, 32) === June 1).
  // Luxon's DateTime.utc(y, m, d) does NOT — it returns Invalid for
  // out-of-range — so we anchor at the 1st and add (d - 1) days. Callers
  // like CalendarWidget.prevWeek pass `cursor.getUTCDate() - 7` which can
  // be negative (e.g. June 3 - 7 = -4); without overflow handling the
  // Invalid fallback dropped users on the INVALID_DAY (1970-01-01) sentinel.
  const dt = DateTime.utc(y, m + 1, 1, h, min, s).plus({ days: d - 1 });
  if (!dt.isValid) {
    console.warn(
      `calendar/utils.utcDate: invalid parts y=${y} m=${m} d=${d} h=${h} min=${min} s=${s}`,
    );
    return INVALID_DAY.toJSDate();
  }
  return dt.toJSDate();
}

function parseBackendIso(iso: string): DateTime {
  const dt = DateTime.fromISO(iso, { zone: "utc" });
  if (!dt.isValid) {
    console.warn(`calendar/utils: invalid ISO string ${iso}`);
    return INVALID_DAY;
  }
  return dt;
}

function toCalendarDate(dateString: string, zone: string): Date {
  const dt = DateTime.fromISO(dateString, { zone: "utc" }).setZone(zone);
  if (!dt.isValid) {
    return INVALID_DAY.toJSDate();
  }
  return utcDate(dt.year, dt.month - 1, dt.day);
}

// holidayDate is a plain calendar date ("YYYY-MM-DD"), not an instant — it must
// never be run through a UTC->zone conversion. Doing so shifts it back a day for
// any timezone behind UTC (e.g. America/New_York), which can misfile a holiday
// that falls on a Monday into the previous week's row.
function toCalendarDateOnly(dateString: string): Date {
  const dt = DateTime.fromISO(dateString, { zone: "utc" });
  if (!dt.isValid) {
    return INVALID_DAY.toJSDate();
  }
  return utcDate(dt.year, dt.month - 1, dt.day);
}

function isWeekStart(date: Date) {
  return fromDayMarker(date).weekday === 1;
}

function startWeek(weeks: Date[][], startDate: Date) {
  return [...weeks, [startDate]];
}

function isInWeek([firstDay]: Date[], date: Date, delta: number) {
  const first = fromDayMarker(firstDay);
  const firstDayIndex = first.weekday === 7 ? 0 : first.weekday;
  const offset =
    delta - (firstDayIndex === 0 ? 6 : firstDayIndex === 1 ? 0 : firstDayIndex);
  const lastDay = DateTime.utc(first.year, first.month, first.day)
    .plus({ days: offset })
    .set({ hour: 23, minute: 59, second: 59 });
  const target = fromDayMarker(date);
  return target >= first && target <= lastDay;
}

function findWeekIndex(weeks: Date[][], date: Date) {
  return weeks.findIndex((week) => isInWeek(week, date, 7));
}

function addToWeek(weeks: Date[][], date: Date) {
  const index = findWeekIndex(weeks, date);

  return index >= 0
    ? [
        ...weeks.slice(0, index),
        [...weeks[index], date],
        ...weeks.slice(index + 1),
      ]
    : [...weeks, [date]];
}

function getWeeks(dates: Date[]) {
  return dates.reduce<Date[][]>(
    (weeks, date) =>
      isWeekStart(date) ? startWeek(weeks, date) : addToWeek(weeks, date),
    [],
  );
}

function getMonthDates(date: Date): Date[] {
  const ref = fromDayMarker(date);
  const daysNbr = ref.daysInMonth ?? 0;
  const dates: Date[] = [];

  for (let i = 1; i <= daysNbr; i++) {
    dates.push(utcDate(ref.year, ref.month - 1, i));
  }

  return dates;
}

function padMonthWeeks(month: Date[][]): Date[][] {
  const firstWeek = new Array<Date>(7);
  const lastWeek = new Array<Date>(7);
  const firstWeekHead = fromDayMarker(month[0][0]);
  const lastWeekHead = fromDayMarker(month[month.length - 1][0]);

  for (let i = 0; i < 7; i++) {
    firstWeek[i] =
      i < 7 - month[0].length
        ? utcDate(
            firstWeekHead.year,
            firstWeekHead.month - 1,
            i - 7 + month[0].length + 1,
          )
        : month[0][i - (7 - month[0].length)];
  }

  for (let i = 0; i < 7; i++) {
    lastWeek[i] =
      i >= month[month.length - 1].length
        ? utcDate(
            lastWeekHead.year,
            lastWeekHead.month - 1,
            i + lastWeekHead.day,
          )
        : month[month.length - 1][i];
  }

  return [firstWeek, ...month.slice(1, month.length - 1), lastWeek];
}

function getMonthsTexts(t: Function) {
  return [
    t("calendar.january"),
    t("calendar.february"),
    t("calendar.march"),
    t("calendar.april"),
    t("calendar.may"),
    t("calendar.june"),
    t("calendar.july"),
    t("calendar.august"),
    t("calendar.september"),
    t("calendar.october"),
    t("calendar.november"),
    t("calendar.december"),
  ];
}

function getDaysTexts(t: Function) {
  return [
    t("gantt.monday"),
    t("gantt.tuesday"),
    t("gantt.wednesday"),
    t("gantt.thursday"),
    t("gantt.friday"),
    t("gantt.saturday"),
    t("gantt.sunday"),
  ];
}

function isEventInDate(date: Date, event: Identifiable<Event>, zone: string) {
  const start = fromDayMarker(toCalendarDate(event.startTime, zone));
  const end = fromDayMarker(toCalendarDate(event.endTime, zone));
  const ref = fromDayMarker(date);
  const day = DateTime.utc(ref.year, ref.month, ref.day);
  return start <= day && end >= day;
}

function isTaskInDate(date: Date, task: Identifiable<Task>, zone: string) {
  if (!task.startDate || !task.endDate) return false;

  const start = fromDayMarker(toCalendarDate(task.startDate, zone));
  const end = fromDayMarker(toCalendarDate(task.endDate, zone));
  const ref = fromDayMarker(date);
  const day = DateTime.utc(ref.year, ref.month, ref.day);
  return start <= day && end >= day;
}

function isUnavailabilityInDate(
  date: Date,
  unavailability: Identifiable<Unavailability>,
  zone: string,
) {
  const start = fromDayMarker(toCalendarDate(unavailability.startDate, zone));
  const end = fromDayMarker(
    toCalendarDate(unavailability.endDate || unavailability.startDate, zone),
  );
  const ref = fromDayMarker(date);
  const day = DateTime.utc(ref.year, ref.month, ref.day);
  return start <= day && end >= day;
}

function isCalendarHolidaysInDate(
  date: Date,
  calendarHolidays: Identifiable<CalendarHolidays>,
) {
  const ref = fromDayMarker(date);
  const day = DateTime.utc(ref.year, ref.month, ref.day);

  return calendarHolidays.holidays.some((holiday) => {
    const h = fromDayMarker(toCalendarDateOnly(holiday.holidayDate));
    return day.hasSame(h, "day");
  });
}

function isMilestoneInDate(
  date: Date,
  milestone: Identifiable<Milestone>,
  zone: string,
) {
  const end = fromDayMarker(toCalendarDate(milestone.endDate, zone));
  const ref = fromDayMarker(date);
  const day = DateTime.utc(ref.year, ref.month, ref.day);
  return end.hasSame(day, "day");
}

function sortCalendarByWeek(
  week: Date[],
  events: (Identifiable<Event> & { project: Identifiable<Project> })[],
  milestones: (Identifiable<Milestone> & { project: Identifiable<Project> })[],
  unavailabilities: Identifiable<Unavailability>[],
  calendarHolidays: Identifiable<CalendarHolidays>[],
  tasks: (Identifiable<Task> & { project: Identifiable<Project> })[],
  zone: string,
) {
  return {
    events: events.filter((event) =>
      week.find((day) => isEventInDate(day, event, zone)),
    ),
    milestones: milestones.filter((milestone) =>
      week.find((day) => isMilestoneInDate(day, milestone, zone)),
    ),
    unavailabilities: unavailabilities.filter((unavailability) =>
      week.find((day) => isUnavailabilityInDate(day, unavailability, zone)),
    ),
    calendarHolidays: calendarHolidays.filter((calendarHolidays) =>
      week.find((day) => isCalendarHolidaysInDate(day, calendarHolidays)),
    ),
    tasks: tasks.filter((task) =>
      week.find((day) => isTaskInDate(day, task, zone)),
    ),
  };
}

function weekDayFromDate(date: Date): number {
  return fromDayMarker(date).weekday;
}

function eventToCalendarEvent(
  event: Identifiable<Event> & { project: Identifiable<Project> },
  week: Date[],
  zone: string,
): CalendarEvent {
  const start = fromDayMarker(toCalendarDate(event.startTime, zone));
  const end = fromDayMarker(toCalendarDate(event.endTime, zone));
  const firstDay = fromDayMarker(week[0]);
  const lastDay = fromDayMarker(week[week.length - 1]);
  const startDay = start.weekday === 7 ? 0 : start.weekday;
  const endDay = end.weekday === 7 ? 0 : end.weekday;

  return {
    ...event,
    from: start <= firstDay ? 1 : startDay === 0 ? 7 : startDay,
    to: end >= lastDay ? 7 : endDay === 0 ? 7 : endDay,
  };
}

function taskToCalendarEvent(
  task: Identifiable<Task> & { project: Identifiable<Project> },
  week: Date[],
  zone: string,
): CalendarTask {
  if (!task.startDate || !task.endDate) {
    return {
      ...task,
      from: 1,
      to: 1,
    };
  }

  const start = fromDayMarker(toCalendarDate(task.startDate, zone));
  const end = fromDayMarker(toCalendarDate(task.endDate, zone));
  const firstDay = fromDayMarker(week[0]);
  const lastDay = fromDayMarker(week[week.length - 1]);
  const startDay = start.weekday === 7 ? 0 : start.weekday;
  const endDay = end.weekday === 7 ? 0 : end.weekday;

  return {
    ...task,
    from: start <= firstDay ? 1 : startDay === 0 ? 7 : startDay,
    to: end >= lastDay ? 7 : endDay === 0 ? 7 : endDay,
  };
}

function unavailabilityToCalendarEvent(
  unavailability: Identifiable<Unavailability>,
  week: Date[],
  zone: string,
): CalendarUnavailability {
  const start = fromDayMarker(toCalendarDate(unavailability.startDate, zone));
  const end = fromDayMarker(
    toCalendarDate(
      unavailability.partialUnavailability
        ? unavailability.startDate
        : unavailability.endDate || unavailability.startDate,
      zone,
    ),
  );
  const firstDay = fromDayMarker(week[0]);
  const lastDay = fromDayMarker(week[week.length - 1]);
  const startDay = start.weekday === 7 ? 0 : start.weekday;
  const endDay = end.weekday === 7 ? 0 : end.weekday;

  return {
    ...unavailability,
    from: start <= firstDay ? 1 : startDay === 0 ? 7 : startDay,
    to: end >= lastDay ? 7 : endDay === 0 ? 7 : endDay,
  };
}

function calendarHolidaysToCalendarEvent(
  calendarHolidays: CalendarHolidays,
  week: Date[],
): CalendarsHolidays {
  const firstDay = fromDayMarker(week[0]);
  const lastDay = fromDayMarker(week[week.length - 1]);

  const filteredHolidays = calendarHolidays.holidays.filter((holiday) => {
    const h = fromDayMarker(toCalendarDateOnly(holiday.holidayDate));
    return h >= firstDay && h <= lastDay;
  });

  const weekdayOf = (iso: string) => {
    const wd = fromDayMarker(toCalendarDateOnly(iso)).weekday;
    return wd === 7 ? 0 : wd;
  };

  const startDay = filteredHolidays.length
    ? Math.min(...filteredHolidays.map((h) => weekdayOf(h.holidayDate)))
    : 0;

  const endDay = filteredHolidays.length
    ? Math.max(...filteredHolidays.map((h) => weekdayOf(h.holidayDate)))
    : 0;

  return {
    ...calendarHolidays,
    holidays: filteredHolidays,
    from: startDay === 0 ? 7 : startDay,
    to: endDay === 0 ? 7 : endDay,
  };
}

function milestoneToCalendarEvent(
  milestone: Identifiable<Milestone> & { project: Identifiable<Project> },
  week: Date[],
  zone: string,
): CalendarMilestone {
  const end = fromDayMarker(toCalendarDate(milestone.endDate, zone));
  const lastDay = fromDayMarker(week[week.length - 1]);
  const endDay = end.weekday === 7 ? 0 : end.weekday;
  const to = end >= lastDay ? 7 : endDay === 0 ? 7 : endDay;

  return {
    ...milestone,
    to,
    from: to,
  };
}

function durationMillis(startIso: string, endIso: string): number {
  const start = parseBackendIso(startIso);
  const end = parseBackendIso(endIso);
  if (!start.isValid || !end.isValid) return 0;
  return end.toMillis() - start.toMillis();
}

function sortEventsByLength(
  events: (Identifiable<Event> & { project: Identifiable<Project> })[],
) {
  return [...events].sort(
    (a, b) =>
      durationMillis(b.startTime, b.endTime) -
      durationMillis(a.startTime, a.endTime),
  );
}

function sortTasksByLength(
  tasks: (Identifiable<Task> & { project: Identifiable<Project> })[],
) {
  return [...tasks].sort((current, next) => {
    if (!current.startDate || !current.endDate) return 1;
    if (!next.startDate || !next.endDate) return -1;
    return (
      durationMillis(next.startDate, next.endDate) -
      durationMillis(current.startDate, current.endDate)
    );
  });
}

function sortUnavailabilitiesByLength(
  unavailabilities: Identifiable<Unavailability>[],
) {
  return [...unavailabilities].sort(
    (a, b) =>
      durationMillis(b.startDate, b.endDate || b.startDate) -
      durationMillis(a.startDate, a.endDate || a.startDate),
  );
}

export {
  utcDate,
  getMonthDates,
  getWeeks,
  padMonthWeeks,
  getMonthsTexts,
  getDaysTexts,
  isEventInDate,
  sortCalendarByWeek,
  eventToCalendarEvent,
  sortEventsByLength,
  isInWeek,
  milestoneToCalendarEvent,
  isMilestoneInDate,
  sortUnavailabilitiesByLength,
  unavailabilityToCalendarEvent,
  isUnavailabilityInDate,
  isCalendarHolidaysInDate,
  calendarHolidaysToCalendarEvent,
  isTaskInDate,
  taskToCalendarEvent,
  sortTasksByLength,
  fromDayMarker,
  weekDayFromDate,
  parseBackendIso,
};
